import { useState, useRef, useCallback, useEffect } from "react";
import AMSListing from "../../../components/listing/AMSListing";
import { useToast } from "../../../components/toast/ToastContext";
import DeletePrompt from "../../../components/DeletePrompt";
import { data, useNavigate } from "react-router-dom";
import {
  useDeleteCustomFieldTemplateMutation,
  useDuplicateCustomFieldTemplateMutation,
  useGetAllDataTypesQuery,
  useGetAllFieldsQuery,
  useGetScopesforFieldsQuery,
  useUpdateCustomFieldTemplateMutation,
} from "../../../api/formsApiSlice";
import { ActionType, Column, DEFAULT_BASIC_CONFIG, FieldConfig, FieldGroupEnum, Field, FieldType, FieldTypeEnum, Scope, FieldDataTypeEnum } from "../../../types/types";
import ConditionalFieldModal from "../ConditionalLogics/ConditionalFieldModal";
import ModalWrapper from "../../../components/ModelWrapper";
import { FieldSettingsModal } from "../../../components/FormBuilder/FieldSettings/FieldSettingsModal";
interface CustomFieldsListingProps {
  onEditForm: (id: number, onSuccess: () => void) => void;
  refreshTrigger?: number;
}

const CustomFieldsListing = ({

  onEditForm,
  refreshTrigger,
}: CustomFieldsListingProps) => {
  const { data: scopesData = [], isLoading: scopesLoading } = useGetScopesforFieldsQuery({ isVisible: true });
  const { data: allDataTypes, isLoading: isLoadingDataTypes } = useGetAllDataTypesQuery({});
  const dataTypes = allDataTypes?.data?.dataTypes || {};
  const dataTypesOptions = Object.keys(dataTypes).map((key) => {
    const value = dataTypes[key];
    const isCheckbox = value === "CHECKBOX";
    return {
      label: isCheckbox ? "Boolean" : key.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()),
      value,
    };
  });
  const staticFormsColumns: Column[] = [
    { id: "id", label: "Id", isSortable: false },
    { id: "name", label: "Name", isSortable: true },
    { id: "dataType", label: "Data Type", },
    { id: "accessKey", label: "Access key", },
    { id: "isStandard", label: "Category", isFlag: true },
    { id: "scope", label: "Scope", isFlag: false },
    { id: "account", label: "Created By", isSortable: true },
    { id: "createdAt", label: "Creation Date", isSortable: true },
  ];

  const filterGroups = [
    {
      key: 'isStandard',
      label: 'Category',
      dataType: 'select',
      options: [
        { label: 'System Fields', value: true },
        { label: 'Custom Fields', value: false },
      ],
    },
    {
      key: 'createdAt',
      label: 'Creation Date',
      dataType: 'date',
      options: [],
    },
    {
      key: 'dataType',
      label: 'Data Type',
      dataType: 'multi-select',
      options: dataTypesOptions,
    },
    // {
    //   key: 'groupName',
    //   label: 'Group Name',
    //   options: Object.values(FieldGroupEnum).map((value) => ({
    //     value,
    //     label: value
    //       .toLowerCase()
    //       .split('_')
    //       .map((word) => word[0].toUpperCase() + word.slice(1))
    //       .join(' '),
    //   })),
    // },
    {
      key: 'scopeId',
      label: 'Scope',
      dataType: 'multi-select',
      options: scopesData?.scopes?.map((scope: Scope) => ({
        value: scope.id,
        label: scope.displayName,
      })) || [],
    },
    // Removed legacy global filter; fields are single-scope now
  ];

  const showToast = useToast();
  const getMutationErrorMessage = useCallback((error: any) => (
    error?.data?.response?.errors ||
    error?.data?.response?.message ||
    error?.data?.message ||
    error?.error ||
    "Unknown error"
  ), []);
  const navigate = useNavigate();
  const [showDeletePrompt, setShowDeletePrompt] = useState(false);
  const [itemId, setItemId] = useState<string | null>(null);
  const [showConditionalFieldsModal, setShowConditionalFieldsModal] = useState(false);
  const [showFieldSettingsModal, setShowFieldSettingsModal] = useState(false);
  const [selectedField, setSelectedField] = useState<Field | null>(null);
  const [showFieldSettingsReadOnly, setShowFieldSettingsReadOnly] = useState(false);
  const [deleteCustomField] = useDeleteCustomFieldTemplateMutation();
  const [duplicateCustomField] = useDuplicateCustomFieldTemplateMutation();
  const [updateCustomField] = useUpdateCustomFieldTemplateMutation({ fixedCacheKey: 'update-custom-field' });
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const lastParamsRef = useRef<{
    page: number;
    perPage: number;
    sort?: { field: string; direction: "ASC" | "DESC" };
    query?: string;
    filters: Record<string, any>;
  }>({
    page: 1,
    perPage: 10,
    filters: { isStandard: false },
  });

  const [initialFilters, setInitialFilters] = useState({
    ...lastParamsRef.current,
    query: "",
  });

  const { sort, filters, ...rest } = initialFilters;

  const params = {
    ...rest,
    ...filters,
    sort: sort?.field,
    sortDir: sort?.direction,
  };
  const {
    data: AllFields,
    refetch,
    isLoading,
    isFetching,
  } = useGetAllFieldsQuery(
    { ...params },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    refetch();
  }, [refreshTrigger, refetch]);

  const handleFieldUpdate = useCallback(async (
    name: string,
    fieldDataType: FieldDataTypeEnum,
    fieldScope: string,
    fieldConfig: FieldConfig,
    accessKey: string
  ) => {
    if (!selectedField?.id) {
      const missingFieldError = new Error('Field id is not available');
      showToast(
        "Update Failed",
        "Field metadata is missing. Please reopen the modal and try again.",
        "error"
      );
      throw missingFieldError;
    }

    try {
      await updateCustomField({
        id: selectedField.id,
        name,
        dataType: fieldDataType,
        scopeId: Number(fieldScope),
        config: fieldConfig,
        accessKey,
      }).unwrap();

      if (!isMountedRef.current) {
        return;
      }

      refetch();
      showToast(
        "Field Updated",
        "The field has been successfully updated.",
        "success"
      );
    } catch (error: any) {
      if (!isMountedRef.current) {
        return;
      }

      const message = getMutationErrorMessage(error);
      showToast(
        "Update Failed",
        message,
        "error"
      );
      throw error;
    }
  }, [selectedField?.id, updateCustomField, refetch, showToast, getMutationErrorMessage]);

  return (
    <>
      <AMSListing
        showFilters
        // showActions
        routeKey="fields"
        noDataTitle="No Field Found"
        noDataSubtitle="Your field list is currently empty. Start adding new fields to keep everything organized and easy to find."
        filterGroups={filterGroups}
        title="Fields"
        subtitle="All fields will show here"
        columns={staticFormsColumns}
        data={AllFields?.fields?.map((field: Field) => ({
          ...field,
          scope: field?.scope?.displayName
        })) || []}
        totalItems={AllFields?.totalCount}
        loading={isLoading || isFetching}
        skeletonHeight={'42px'}
        onAction={useCallback(
          async (action: ActionType, id: string, ...rest: any[]) => {
            const numericId = Number(id);
            if (action === "delete") {
              setItemId(id);
              setShowDeletePrompt(true);
              return;
            }
            if (action === "copy") {
              try {
                await duplicateCustomField(numericId).unwrap();
                refetch();
                showToast(
                  "Field Duplicated",
                  "The field has been successfully duplicated.",
                  "success"
                );
              } catch (error) {
                showToast(
                  "Duplication Failed",
                  "Failed to duplicate field. Please try again.",
                  "error"
                );
              }
              return;
            }
            if (action === "edit") {
              const fieldToEdit = AllFields?.fields.find((field: Field) => field.id === numericId);
              if (fieldToEdit) {
                setSelectedField(fieldToEdit);
                setShowFieldSettingsModal(true);
                setShowFieldSettingsReadOnly(false);
              }
              return;
            }
            if (action === "view") {
              const fieldToView = AllFields?.fields.find((field: Field) => field.id === numericId);
              if (fieldToView) {
                setSelectedField(fieldToView);
                setShowFieldSettingsModal(true);
                setShowFieldSettingsReadOnly(true);
              }
              return;
            }
            if (action === "condition") {
              setItemId(id);
              setShowConditionalFieldsModal(true);
              return;
            }
          },
          [refetch, duplicateCustomField, showToast, AllFields?.fields]
        )}
        setInitialFilters={setInitialFilters}
        defaultFilters={{ isStandard: false }}
      />

      {showDeletePrompt && (
        <DeletePrompt onConfirm={async () => {
          if (!itemId) return;
          const numericId = Number(itemId);
          setShowDeletePrompt(false);

          try {
            const { data }: any = await deleteCustomField(numericId);
            if (data) {
              refetch();
              showToast(
                "Custom Field Deleted",
                "The selected field has been deleted.",
                "success"
              );
            }
          } catch {
            showToast(
              "Delete Failed",
              "Failed to delete field. Please try again.",
              "error"
            );
          }

          setItemId(null);
        }} onCancel={() => {
          setShowDeletePrompt(false);
          setItemId(null);
        }} />
      )}

      {showConditionalFieldsModal && (
        <ModalWrapper onClose={() => setShowConditionalFieldsModal(false)}>
          <ConditionalFieldModal
            onClose={() => setShowConditionalFieldsModal(false)}
            onSave={() => {
              setShowConditionalFieldsModal(false);
              refetch();
            }}
            fieldId={itemId !== null ? Number(itemId) : undefined}
            controllingFields={
              AllFields?.fields.find((field: any) => field?.id === Number(itemId))?.controllingField || []
            }
          />
        </ModalWrapper>
      )}

      {showFieldSettingsModal &&
        <FieldSettingsModal
          show={showFieldSettingsModal}
          onHide={() => {
            setShowFieldSettingsModal(false);
            setSelectedField(null);
            setShowFieldSettingsReadOnly(false);
          }}
          fieldId={selectedField?.id}
          config={selectedField?.config || DEFAULT_BASIC_CONFIG}
          fieldType={selectedField?.fieldType as FieldType}
          fieldDataType={selectedField?.dataType as FieldDataTypeEnum}
          fieldAccessKey={selectedField?.accessKey || null}
          fieldName={selectedField?.name}
          fieldScope={ selectedField?.scopeId ? selectedField?.scopeId.toString() : ''}
          readOnly={showFieldSettingsReadOnly}
          onSave={(config: FieldConfig) => { console.log("onSave", config); }}
          onSaveForUpdate={handleFieldUpdate}
          isCreating={false}
        />
      }
    </>
  );
};

export default CustomFieldsListing;
